import React, { FunctionComponent } from 'react'
import { Button, Form, Input, Select, Typography } from 'antd'
import CustomModal from '../../helpers/CustomModal'
import { getCountryCallingCode, isValidPhoneNumber } from 'react-phone-number-input'
import { DEFAULT_COUNTRY } from '../../../constants'
import Countries from '../../../utils/countries'
import { ICall } from '../../../context/types'

const { TextArea } = Input
const { Option } = Select
const { Title } = Typography

type TProps = {
	visible: boolean
	participant: ICall['participant']
	onSave: (data: { message: string; email: string; phone: string }) => void
	onClose: () => void
}

const ReportIssueModal: FunctionComponent<TProps> = ({ visible, participant, onSave, onClose }) => {
	const [form] = Form.useForm()
	const onCloseModal = () => {
		onClose()
		form.resetFields()
	}

	const onSend = () => {
		const formData = form.getFieldsValue()
		onSave({
			message: formData.message,
			email: formData.email,
			phone: formData.phone_country_id && formData.phone ? `+${formData.phone_country_id}${formData.phone}` : undefined,
		})
		form.resetFields()
	}

	const triggerValidatePhoneField = (option: string) => {
		const values = form.getFieldsValue()
		form.setFieldsValue({
			...values,
			phone_country_id: getCountryCallingCode(option),
		})
		form.validateFields(['phone'])
	}

	const prefixSelector = (
		<>
			<Form.Item name="phone_country_code" noStyle>
				<Select
					filterOption={(input, option) => {
						return (
							option['data-code'].toLowerCase().indexOf(input.toLowerCase().replace('+', '')) >= 0 ||
							option.value.toLowerCase().indexOf(input.toLowerCase()) >= 0
						)
					}}
					showSearch
					style={{ width: 105 }}
					onChange={triggerValidatePhoneField}
				>
					{Countries.options.map(({ code, flag, country }, index) => (
						<Option key={index} value={`${country}`} data-code={code}>
							<span className="country-flag">{flag}</span>
							<span className="country-code">+{code}</span>
						</Option>
					))}
				</Select>
			</Form.Item>
			<Form.Item name="phone_country_id" style={{ display: 'none' }}>
				<Input />
			</Form.Item>
		</>
	)

	return (
		<CustomModal
			style={{ zIndex: 3 }}
			visible={visible}
			title="Report a problem with a call"
			width={620}
			onCancel={onCloseModal}
		>
			<Form
				name="RescheduleForm"
				initialValues={{
					email: participant.email,
					phone: participant.phone,
					phone_country_code: participant.phoneCountryCode || DEFAULT_COUNTRY.iso,
					phone_country_id: participant.phoneCountryId,
				}}
				form={form}
				onFinish={onSend}
				scrollToFirstError
				layout="vertical"
			>
				<Form.Item
					name="message"
					label="Message"
					rules={[{ required: true, message: 'Please fill information about problem' }]}
				>
					<TextArea style={{ height: '100px', resize: 'none' }} />
				</Form.Item>

				<Title level={5}>Contact information</Title>
				<Form.Item
					name="email"
					label="Your email"
					rules={[{ type: 'email', required: true, message: 'The input is not valid email address!' }]}
				>
					<Input />
				</Form.Item>
				<Form.Item
					name="phone"
					label="Your mobile number"
					rules={[
						{
							validator: (_rule, value) => {
								const phoneCountryId = form.getFieldValue('phone_country_id')
								if (!value || isValidPhoneNumber(`+${phoneCountryId}${value}`)) {
									return Promise.resolve()
								}
								return Promise.reject()
							},
							message: 'The input is not valid mobile number!',
						},
					]}
				>
					<Input addonBefore={prefixSelector} style={{ width: '100%' }} type="number" />
				</Form.Item>
				<div style={{ textAlign: 'right', marginTop: '10px' }}>
					<Button type="primary" shape="round" htmlType="submit">
						Send
					</Button>
				</div>
			</Form>
		</CustomModal>
	)
}

export default ReportIssueModal
